export type ExecutionStatusFilter =
  | 'all'
  | 'executed'
  | 'running'
  | 'queued'
  | 'failed'
  | 'cancelled';

export type VisibilityScoreFilter = 'all' | '0-20' | '20-40' | '40-60' | '60-80' | '80-100';

/** @deprecated Use VisibilityScoreFilter — filters visibility score %, not time range. */
export type VisibilityRangeFilter = VisibilityScoreFilter;

export interface ExecutionFiltersState {
  search: string;
  status: ExecutionStatusFilter;
  provider: string;
  visibility: VisibilityScoreFilter;
  brand: string;
}

export const DEFAULT_EXECUTION_FILTERS: ExecutionFiltersState = {
  search: '',
  status: 'all',
  provider: 'all',
  visibility: 'all',
  brand: '',
};

export function normalizeSearchQuery(query: string): string {
  return query.trim().toLowerCase();
}

export function matchesSearch(
  query: string,
  ...values: Array<string | null | undefined | number>
): boolean {
  if (!query) return true;
  return values.some((value) => {
    if (value == null) return false;
    return String(value).toLowerCase().includes(query);
  });
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null) return '—';
  return `${(ms / 1000).toFixed(1)} s`;
}

export function isExecutedExecutionStatus(status: string): boolean {
  const normalized = status.toLowerCase();
  return normalized === 'completed' || normalized === 'executed' || normalized === 'success';
}

export type ExecutionDisplayStatus = 'executed' | 'failed' | 'pending';

export function resolveExecutionDisplayStatus(status: string): ExecutionDisplayStatus {
  const normalized = status.toLowerCase();
  if (isExecutedExecutionStatus(status)) return 'executed';
  if (normalized === 'failed' || normalized === 'error') return 'failed';
  return 'pending';
}

/** Visibility is only meaningful when the run completed and at least one brand was detected. */
export function formatExecutionVisibility(row: {
  status: string;
  visibilityScore: number;
  brandsFound: number;
}): string {
  if (!isExecutedExecutionStatus(row.status) || row.brandsFound === 0) return '—';
  return `${row.visibilityScore}%`;
}

export function matchesStatusFilter(status: string, filter: ExecutionStatusFilter): boolean {
  if (filter === 'all') return true;
  const normalized = status.toLowerCase();
  switch (filter) {
    case 'executed':
      return normalized === 'completed' || normalized === 'executed' || normalized === 'success';
    case 'running':
      return normalized === 'running' || normalized === 'in_progress' || normalized === 'processing';
    case 'queued':
      return normalized === 'queued' || normalized === 'pending';
    case 'failed':
      return normalized === 'failed' || normalized === 'error';
    case 'cancelled':
      return normalized === 'cancelled' || normalized === 'canceled';
    default:
      return true;
  }
}

export function matchesVisibilityRange(score: number, range: VisibilityScoreFilter): boolean {
  if (range === 'all') return true;
  const [minStr, maxStr] = range.split('-');
  const min = Number(minStr);
  const max = Number(maxStr);
  return score >= min && score <= max;
}

export function providerMatches(
  provider: string | null | undefined,
  model: string | null | undefined,
  filter: string,
): boolean {
  if (filter === 'all') return true;
  const needle = filter.toLowerCase();
  const candidates = [provider, model].filter(Boolean).map((v) => String(v).toLowerCase());
  if (needle === 'chatgpt') {
    return candidates.some(
      (c) => c.includes('chatgpt') || c.includes('openai') || c.includes('gpt'),
    );
  }
  return candidates.some((c) => c.includes(needle));
}
